import { Box, Button, Skeleton, Stack, Typography } from "@mui/joy";
import {
    Banknote,
    CircleDollarSign,
    Layers,
    Package,
    Percent,
    PiggyBank,
    RefreshCw,
    Waves,
} from "lucide-react";
import type { ReactNode } from "react";

import useFinanceBreakdown, {
    formatFinanceCurrency,
    type ContractPricingResult,
} from "../../hooks/useFinanceBreakdown";
import {
    materialGlassPanelSx,
    materialRowDividerSx,
} from "./materialRowLayout";

type BreakdownRow = {
    label: string;
    hint?: string;
    amount: string;
    muted?: boolean;
    emphasize?: boolean;
};

type SectionPanelProps = {
    icon: ReactNode;
    title: string;
    subtitle?: string;
    children: ReactNode;
};

function SectionPanel({ icon, title, subtitle, children }: SectionPanelProps) {
    return (
        <Box sx={materialGlassPanelSx}>
            <Box
                sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: 1.25,
                    ...materialRowDividerSx,
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                        sx={{
                            display: "flex",
                            color: "text.secondary",
                            "& svg": { display: "block" },
                        }}
                    >
                        {icon}
                    </Box>
                    <Box>
                        <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </Box>
            {children}
        </Box>
    );
}

function BreakdownLine({ label, hint, amount, muted, emphasize }: BreakdownRow) {
    return (
        <Box
            sx={{
                px: { xs: 2, sm: 2.5 },
                py: 1.25,
                ...materialRowDividerSx,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                bgcolor: emphasize ? "primary.softBg" : undefined,
                "&:last-of-type": { borderBottom: "none" },
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    level="body-sm"
                    sx={{
                        fontWeight: muted || emphasize ? 700 : 500,
                        color: emphasize
                            ? "primary.plainColor"
                            : muted
                              ? "text.primary"
                              : "text.secondary",
                    }}
                >
                    {label}
                </Typography>
                {hint && (
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                        {hint}
                    </Typography>
                )}
            </Box>
            <Typography
                level="body-sm"
                sx={{
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                    color: emphasize
                        ? "primary.plainColor"
                        : muted
                          ? "text.primary"
                          : "text.secondary",
                    flexShrink: 0,
                }}
            >
                {amount}
            </Typography>
        </Box>
    );
}

function SummaryStat({
    icon,
    label,
    value,
}: {
    icon: ReactNode;
    label: string;
    value: string;
}) {
    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 140,
                p: 1.5,
                borderRadius: "4px",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.surface",
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center" mb={0.75}>
                <Box sx={{ color: "text.tertiary", display: "flex" }}>{icon}</Box>
                <Typography level="body-xs" sx={{ color: "text.tertiary", fontWeight: 600 }}>
                    {label}
                </Typography>
            </Stack>
            <Typography
                level="title-md"
                sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
            >
                {value}
            </Typography>
        </Box>
    );
}

function percentLabel(rate: number): string {
    const pct = rate * 100;
    return `${pct.toFixed(pct % 1 === 0 ? 0 : 2)}%`;
}

function buildSections(pricing: ContractPricingResult) {
    const d = pricing.Details;
    const money = formatFinanceCurrency;
    const markupTotal =
        d.profitMarginAmount + d.generalInsuranceAmount + d.jobOverheadAmount;

    return {
        summary: {
            roof: pricing.RoofPriceFormatted,
            gutter: pricing.GutterPriceFormatted,
            markup: money(markupTotal),
            contract: pricing.TotalPriceFormatted,
        },
        roofRows: [
            {
                label: "Roof materials",
                hint: "Cart lines with supplier selected",
                amount: money(d.roofMaterials),
            },
            {
                label: `Sales tax on roof materials (${percentLabel(d.salesTaxRate)})`,
                amount: money(d.roofSalesTax),
            },
            {
                label: "Roof labor",
                hint: "Installation labor",
                amount: money(d.roofLabor),
            },
            {
                label: "Roof price",
                amount: money(pricing.RoofPrice),
                muted: true,
            },
        ] satisfies BreakdownRow[],
        gutterRows: [
            {
                label: "Gutter & downspout materials",
                hint: "Gutters, downspouts, screens",
                amount: money(d.gutterMaterials),
            },
            {
                label: "Gutter & downspout labor",
                amount: money(d.gutterLabor),
            },
            {
                label: "Gutter price",
                amount: money(pricing.GutterPrice),
                muted: true,
            },
        ] satisfies BreakdownRow[],
        markupRows: [
            ...(d.bidAmountOverride != null && d.bidAmountOverride > 0
                ? [
                      {
                          label: "Manual contract amount (override)",
                          hint: "EST bid amount override is active",
                          amount: money(d.bidAmountOverride),
                          muted: true,
                      } satisfies BreakdownRow,
                  ]
                : [
                      {
                          label: `Profit margin (${percentLabel(d.targetMarginPercent)})`,
                          amount: money(d.profitMarginAmount),
                      },
                      {
                          label: `General insurance (${percentLabel(d.overheadPercent)})`,
                          amount: money(d.generalInsuranceAmount),
                      },
                      {
                          label: `Overhead (${percentLabel(d.jobOverheadPercent)})`,
                          amount: money(d.jobOverheadAmount),
                      },
                  ]),
            {
                label: "Scope subtotal (roof + gutter)",
                amount: money(d.scopeSubtotal),
                muted: true,
            },
        ] satisfies BreakdownRow[],
        totalRows: [
            {
                label: "Contract total",
                amount: pricing.TotalPriceFormatted,
                emphasize: true,
            },
            ...(pricing.UpgradeAmount
                ? [
                      {
                          label: "Shingle upgrade",
                          hint: pricing.UpgradeNotes || undefined,
                          amount: pricing.UpgradeAmount,
                      } satisfies BreakdownRow,
                  ]
                : []),
            {
                label: "Payment schedule",
                hint: pricing.PaymentSchedule || "Not set in estimating",
                amount: "",
            },
        ] satisfies BreakdownRow[],
    };
}

export default function FinanceBreakdown({ projectId }: { projectId?: string }) {
    const { pricing, loading, error, reload } = useFinanceBreakdown(projectId);

    if (loading) {
        return (
            <Box sx={{ mt: 1 }}>
                <Stack spacing={2}>
                    <Skeleton variant="rectangular" height={72} />
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} variant="rectangular" height={140} />
                    ))}
                </Stack>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ mt: 2, py: 3, textAlign: "center" }}>
                <Typography level="body-sm" color="danger" sx={{ mb: 1.5 }}>
                    {error}
                </Typography>
                <Button
                    size="sm"
                    variant="soft"
                    startDecorator={<RefreshCw size={14} />}
                    onClick={() => reload()}
                    sx={{ borderRadius: "4px" }}
                >
                    Retry
                </Button>
            </Box>
        );
    }

    if (!pricing) {
        return (
            <Box sx={{ mt: 2, py: 3, textAlign: "center" }}>
                <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                    No pricing data yet. Select suppliers on materials and labor, then refresh.
                </Typography>
            </Box>
        );
    }

    const sections = buildSections(pricing);

    return (
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                }}
            >
                <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                    Same pricing engine as contract preview. Only cart lines with a supplier
                    selected are included.
                </Typography>
                <Button
                    size="sm"
                    variant="plain"
                    color="neutral"
                    startDecorator={<RefreshCw size={14} />}
                    onClick={() => reload()}
                    sx={{ borderRadius: "4px", flexShrink: 0 }}
                >
                    Refresh
                </Button>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                <SummaryStat
                    icon={<Package size={14} />}
                    label="Roof"
                    value={sections.summary.roof}
                />
                <SummaryStat
                    icon={<Waves size={14} />}
                    label="Gutters"
                    value={sections.summary.gutter}
                />
                <SummaryStat
                    icon={<Percent size={14} />}
                    label="Markup"
                    value={sections.summary.markup}
                />
                <SummaryStat
                    icon={<CircleDollarSign size={14} />}
                    label="Contract"
                    value={sections.summary.contract}
                />
            </Box>

            <SectionPanel
                icon={<Layers size={16} />}
                title="Direct costs — roof"
                subtitle="Materials, sales tax, and installation labor"
            >
                {sections.roofRows.map((row) => (
                    <BreakdownLine key={row.label} {...row} />
                ))}
            </SectionPanel>

            <SectionPanel
                icon={<Waves size={16} />}
                title="Gutters & downspouts"
                subtitle="Gutter scope materials and labor from cart"
            >
                {sections.gutterRows.map((row) => (
                    <BreakdownLine key={row.label} {...row} />
                ))}
            </SectionPanel>

            <SectionPanel
                icon={<PiggyBank size={16} />}
                title="Markup & fees"
                subtitle="Profit, insurance, and overhead on scope subtotal"
            >
                {sections.markupRows.map((row) => (
                    <BreakdownLine key={row.label} {...row} />
                ))}
            </SectionPanel>

            <SectionPanel
                icon={<Banknote size={16} />}
                title="Customer totals"
                subtitle="Contract total and payment schedule"
            >
                {sections.totalRows.map((row) =>
                    row.amount ? (
                        <BreakdownLine key={row.label} {...row} />
                    ) : (
                        <Box
                            key={row.label}
                            sx={{
                                px: { xs: 2, sm: 2.5 },
                                py: 1.25,
                                ...materialRowDividerSx,
                                "&:last-of-type": { borderBottom: "none" },
                            }}
                        >
                            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                {row.label}
                            </Typography>
                            <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.25 }}>
                                {row.hint}
                            </Typography>
                        </Box>
                    ),
                )}
            </SectionPanel>
        </Box>
    );
}
